import * as tslib_1 from "tslib";
import { mergeMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { EntityQuery } from 'breeze-client';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { BreezeService } from '../core/breeze.service';
import { AlertsService } from '../core/alerts.service';
import { DomainService } from '../domain.service';
import { EntityManagerService } from '../entity-manager.service';
import { RangeValidator } from '../core/validators/range.validator';
var ForecastService = /** @class */ (function (_super) {
    tslib_1.__extends(ForecastService, _super);
    /**
     * Creates an instance of ForecastService.
     * @param {AlertsService} alertsService The alerts service.
     * @param {TranslateService} translateService The translate service.
     * @memberOf ForecastService
     */
    function ForecastService(alertsService, translateService, entityManagerService) {
        var _this = _super.call(this, alertsService, translateService, entityManagerService) || this;
        /**
         * Supported forecast entity types.
         * @private
         * @type {{ [key: string]: Function }
         */
        _this.registerEntityTypes = {};
        /**
         * A value indicating whether the service is waiting for a call from the backend saying the calculation or planning is completed.
         * @private
         * @memberOf ForecastService
         */
        _this._isExecuting = false;
        /**
         * A value indicating whether the service is waiting for a call from the backend saying the calculation or planning is completed.
         * @private
         * @memberOf ForecastService
         */
        _this._isCalculating = false;
        /**
         * Will be fired when this service is done planning or calculating the forecast.
         * @memberOf ForecastService
         */
        _this.processCompleted = new Subject();
        _this.initialize('forecast');
        if (_this.entityManager && _this.metadataStore) {
            ForecastService.entityTypes = _this.entityManager.metadataStore.getEntityTypes();
            Object.keys(_this.registerEntityTypes).forEach(function (entityType) {
                _this.metadataStore.registerEntityTypeCtor(entityType, _this.registerEntityTypes[entityType]);
            });
            _this.registerValidator('Correction', 'Value', RangeValidator(0, Infinity));
            _this.registerValidator('Shrinkage', 'Value', RangeValidator(0, 99));
        }
        return _this;
    }
    Object.defineProperty(ForecastService.prototype, "isExecuting", {
        /**
         * Getter and setter a value indicating whether the service is waiting for a calculation or planning to be completed.
         * @readonly
         * @type {boolean}
         * @memberOf ForecastService
         */
        get: function () {
            return this._isExecuting;
        },
        set: function (value) {
            this._isExecuting = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ForecastService.prototype, "isCalculating", {
        /**
         * Gets a value indicating whether the service is waitinf for a calculation or planning to be completed.
         * @readonly
         * @type {boolean}
         * @memberOf ForecastService
         */
        get: function () {
            return this._isCalculating;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns the expected service level according to the Erlang C formula.
     * @static
     * @param {number} resources The number of call center agents.
     * @param {number} calls The expected number of calls.
     * @param {moment.Duration} timeLength The length of time in which the number of calls are expected.
     * @param {moment.Duration} aht The average handling time.
     * @param {moment.Duration} maxWait The maximum time a calling customer may wait to still be within the SLA.
     * @returns {number} The expected service level given all the parameters.
     * @memberOf ForecastService
     */
    ForecastService.getServiceLevel = function (resources, calls, timeLength, aht, maxWait) {
        if (calls === 0) {
            return 1;
        }
        // Traffic intensity.
        var erlang = aht.asSeconds() * calls / timeLength.asSeconds();
        // If there are fewer resources than that, there will be more calls coming in than there are resources to answer them.
        if (erlang > resources) {
            return 0;
        }
        // Erlang Loss Function, or probability of blockage, is the simple measurement which does not take the random timing of incoming calls into account.
        var erlangB = 1;
        for (var r = 0; r < resources; r++) {
            erlangB += Math.pow(erlang, r) / ForecastService.factorial(r);
        }
        // The call traffic per resource.
        var epr = erlang / resources;
        var F = Math.pow(erlang, resources) / ForecastService.factorial(resources);
        var erlangC = F / (F + (1 - epr) * erlangB);
        // The probability of calls answered before the maximum waiting time.
        var result = 1 - erlangC * Math.exp(-(resources - erlang) * maxWait.asSeconds() / aht.asSeconds());
        return result;
    };
    /**
     * Returns the factorial of the parameter.
     * @private
     * @static
     * @param {number} n n! is returned.
     * @returns {number} The factorial. That is 1 * 2 * ... * n.
     * @memberOf ForecastService
     */
    ForecastService.factorial = function (n) {
        if (n < 0) {
            return NaN;
        }
        var result = 1;
        for (var i = 2; i <= n; i++) {
            result *= i;
        }
        return result;
    };
    /**
     * Sets default values for every newly created entity (just after creation).
     * Specially for non standard (Symagic Server) entity types.
     * @param item The entity to update.
     * @memberOf ForecastService
     */
    ForecastService.prototype.setDefaultValues = function (user, item) {
        _super.prototype.setDefaultValues.call(this, user, item);
        if (item.entityType.shortName === 'SpecialDay') {
            item['EffectDuration'] = 1;
        }
    };
    /**
     * Sets the domain  master key of a forecast entities.
     * @param masterType The domain entity type.
     * @param masterOid The OID of the domain entity type.
     * @param detail The forecast entity.
     * @returns The forecast entity with the master key set.
     * @memberOf ForecastService
     */
    ForecastService.prototype.setupMasterDetail = function (masterType, masterOid, detail) {
        switch (masterType.shortName) {
            case 'Calendar':
                if (detail.entityType.shortName !== 'SpecialDay') {
                    this.alertsService.createAlert('warning', 'APP.FORECAST.SPECIALDAYS_EXPECTED', detail.entityType.shortName);
                    return detail;
                }
                detail['CalendarKey'] = DomainService.oidToKey(masterOid);
                break;
            case 'Function':
                if (detail.entityType.shortName !== 'Divider') {
                    this.alertsService.createAlert('warning', 'APP.FORECAST.DIVIDERS_EXPECTED', detail.entityType.shortName);
                    return detail;
                }
                detail['FunctionKey'] = DomainService.oidToKey(masterOid);
                break;
            default:
                this.alertsService.createAlert('warning', 'APP.FORECAST.NO_FORECAST_RELATION', masterType.shortName);
                break;
        }
        return detail;
    };
    /**
     * The execute flow
     * @param {Entity} template The shift template entity
     * @returns {Promise<void>}
     * @memberOf ForecastService
     */
    ForecastService.prototype.execute = function (domainService, funct, template, script, start, finish) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var query, result, forecast, date, error_1;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (this.isExecuting || this._isCalculating) {
                            this.processDone('APP.FORECAST.EXECUTION_OR_CALCULATION_IS_RUNNING', false);
                            return [2 /*return*/];
                        }
                        if (!template) {
                            this.processDone('APP.FORECAST.NO_TEMPLATE_SELECTED', false);
                            return [2 /*return*/];
                        }
                        this.isExecuting = true;
                        query = EntityQuery.from('Plan');
                        query.parameters = { functionOid: funct['Oid'], templateOid: template['Oid'], start: start.toDate(), finish: finish.toDate() };
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 4, , 5]);
                        return [4 /*yield*/, this.entityManager.executeQuery(query)];
                    case 2:
                        result = _a.sent();
                        if (!result || result.results.length === 0) {
                            this.processDone('APP.FORECAST.EXECUTION_UNSUCCESSFUL', false, 'APP.FORECAST.COPYING_TEMPLATE_FAILED');
                            return [2 /*return*/];
                        }
                        if (!script) {
                            this.processDone('APP.FORECAST.EXECUTION_COMPLETED', true);
                            return [2 /*return*/];
                        }
                        forecast = result.results[0];
                        date = new Date(forecast['Start']);
                        date.setHours(0, 0, 0, 0);
                        return [4 /*yield*/, this.startRunscript(domainService, script, [forecast['Oid']], date)];
                    case 3:
                        _a.sent();
                        return [3 /*break*/, 5];
                    case 4:
                        error_1 = _a.sent();
                        this.processDone(null, false, error_1);
                        return [3 /*break*/, 5];
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * The calculate flow
     * @param {Entity} item The shift template entity
     * @returns {Promise<void>}
     * @memberOf ForecastService
     */
    ForecastService.prototype.calculate = function (item, start, finish) {
        var _this = this;
        if (this.isExecuting || this._isCalculating) {
            this.processDone('APP.FORECAST.EXECUTION_OR_CALCULATION_IS_RUNNING', false);
            return;
        }
        this._isCalculating = true;
        var query = EntityQuery.from('Calculate');
        query.parameters = { oid: item['Oid'], start: start.toDate(), finish: finish.toDate() };
        this.entityManager.executeQuery(query).then(function (result) {
            if (result) {
                _this.processDone('APP.FORECAST.CALCULATION_COMPLETED', true);
            }
            else {
                _this.processDone('APP.FORECAST.CALCULATION_UNSUCCESSFUL', false, 'APP.FORECAST.CALIBRATION_FAILED');
            }
        }).catch(function (error) {
            // Issue: Server errors forecast #2940
            // this.processDone('APP.FORECAST.CALCULATION_UNSUCCESSFUL', false, error);
            _this.processDone(null, false, error); // Centrale error handler geeft alert.
        });
    };
    /**
     * Sets indicators to show the service is not waiting anymore and fires the event that indicates as such.
     * @private
     * @param {string} alert The label is displayed in an alert.
     * @param {boolean} success A value indicating whether the process was completed successfully.
     * @param {*} [error] Errors, if any.
     * @memberOf ForecastService
     */
    ForecastService.prototype.processDone = function (alert, success, error) {
        if (alert) { // Om dubbele alert te kunnen verkomen wanneer centrale error handler dit op pakt.
            this.alertsService.createAlert(success ? 'success' : 'warning', alert, error);
        }
        this._isCalculating = false;
        this.isExecuting = false;
        this.processCompleted.next(success);
    };
    /**
     * Runs the script
     * @private
     * @param {BreezeService} domainService The domain service is needed to save changes and have the plan macro method invoked.
     * @param {PlanMacro} script The script.
     * @param {any} [parameters=[]] Parameters passed on to the method.
     * @param {Date} [date] The parameter for the MoveTo method.
     * @memberOf ForecastService
     */
    ForecastService.prototype.startRunscript = function (domainService, script, parameters, date) {
        if (parameters === void 0) { parameters = []; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var error_2;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 6, , 7]);
                        return [4 /*yield*/, this.startReplaceSubjectWith(domainService, script, parameters, date)];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, this.startMoveTo(domainService, script, date)];
                    case 2:
                        _a.sent();
                        return [4 /*yield*/, this.startScriptInit(domainService, script)];
                    case 3:
                        _a.sent();
                        return [4 /*yield*/, this.startScriptRun(domainService, script)];
                    case 4:
                        _a.sent();
                        return [4 /*yield*/, this.scriptInProgress(script)];
                    case 5:
                        _a.sent();
                        this.processDone('APP.FORECAST.EXECUTION_COMPLETED', true);
                        return [3 /*break*/, 7];
                    case 6:
                        error_2 = _a.sent();
                        this.processDone('APP.FORECAST.EXECUTION_UNSUCCESSFUL', false, error_2);
                        return [3 /*break*/, 7];
                    case 7: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Starts the invoke
     * @private
     * @param {BreezeService} domainService The domain service is needed to save changes and have the plan macro method invoked.
     * @param {PlanMacro} entity The entity.
     * @param {string} method The script method is executed.
     * @param {any} [parameters=[]] Parameters passed on to the method.
     * @memberOf ForecastService
     */
    ForecastService.prototype.startInvokeMethod = function (domainService, entity, method, parameters) {
        if (parameters === void 0) { parameters = []; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                return [2 /*return*/, new Promise(function (resolve, reject) {
                        _this.invokeSubscription = BreezeService.invokeMethod(entity, method, parameters)
                            .pipe(mergeMap(function () {
                            return domainService.saveChanges(function (error) {
                                if (error) {
                                    return reject(new Error(error));
                                }
                            });
                        })).subscribe(function (response) {
                            var lastResult = response.entities.find(function (foundEntity) { return foundEntity['Origin'] === entity.Oid && foundEntity['entityType'] == null; });
                            if (!lastResult || !lastResult['Value']) {
                                // this.processDone('APP.FORECAST.NO_SCRIPT_RESULT', false, method);
                                return reject(new Error(method));
                            }
                            resolve(lastResult);
                        }, function () { return _this.invokeSubscription.unsubscribe(); });
                        _this.subscriptionPool.add(_this.invokeSubscription);
                    })];
            });
        });
    };
    /**
     * Runs the ReplaceSubjectsWith script
     * @private
     * @param {BreezeService} domainService The domain service is needed to save changes and have the plan macro method invoked.
     * @param {PlanMacro} entity The entity.
     * @param {any} [parameters=[]] Parameters passed on to the method.
     * @param {Date} [date] The parameter for the MoveTo method.
     * @memberOf ForecastService
     */
    ForecastService.prototype.startReplaceSubjectWith = function (domainService, entity, parameter, date) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var lastResult;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.startInvokeMethod(domainService, entity, 'ReplaceSubjectsWith', parameter)];
                    case 1:
                        lastResult = _a.sent();
                        return [2 /*return*/, new Promise(function (resolve, reject) {
                                if (!moment.isDate(date)) {
                                    return reject(new Error('Invalid parameter for MoveTo: ' + date));
                                }
                                if (lastResult['Name'] !== 'ReplaceSubjectsWith') {
                                    return reject(new Error('ReplaceSubjectsWith'));
                                }
                                resolve();
                            })];
                }
            });
        });
    };
    /**
     * Runs the MoveTo script
     * @private
     * @param {BreezeService} domainService The domain service is needed to save changes and have the plan macro method invoked.
     * @param {PlanMacro} screntityipt The entity.
     * @param {Date} [date] The parameter for the MoveTo method.
     * @memberOf ForecastService
     */
    ForecastService.prototype.startMoveTo = function (domainService, entity, date) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var lastResult;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.startInvokeMethod(domainService, entity, 'MoveTo', [date])];
                    case 1:
                        lastResult = _a.sent();
                        return [2 /*return*/, new Promise(function (resolve, reject) {
                                if (lastResult['Name'] !== 'MoveTo') {
                                    return reject(new Error('MoveTo'));
                                }
                                resolve();
                            })];
                }
            });
        });
    };
    /**
     * Runs the ScriptInit script
     * @private
     * @param {BreezeService} domainService The domain service is needed to save changes and have the plan macro method invoked.
     * @param {PlanMacro} entity The entity.
     * @memberOf ForecastService
     */
    ForecastService.prototype.startScriptInit = function (domainService, entity) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var lastResult;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.startInvokeMethod(domainService, entity, 'ScriptInit', [])];
                    case 1:
                        lastResult = _a.sent();
                        return [2 /*return*/, new Promise(function (resolve, reject) {
                                if (lastResult['Name'] !== 'ScriptInit') {
                                    return reject(new Error('ScriptInit'));
                                }
                                resolve();
                            })];
                }
            });
        });
    };
    /**
     * Runs the ScriptRun script
     * @private
     * @param {BreezeService} domainService The domain service is needed to save changes and have the plan macro method invoked.
     * @param {PlanMacro} entity The entity.
     * @memberOf ForecastService
     */
    ForecastService.prototype.startScriptRun = function (domainService, entity) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var lastResult;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.startInvokeMethod(domainService, entity, 'ScriptRun', [500])];
                    case 1:
                        lastResult = _a.sent();
                        return [2 /*return*/, new Promise(function (resolve, reject) {
                                if (lastResult['Value'] === 'FINISHED') {
                                    resolve();
                                }
                                if (lastResult['Value'] === 'INITIALIZING' || lastResult['Value'] === 'RUNNING') {
                                    resolve();
                                }
                                if (lastResult['Name'] !== 'ScriptRun') {
                                    return reject(new Error('ScriptRun'));
                                }
                            })];
                }
            });
        });
    };
    ForecastService.prototype.scriptInProgress = function (script) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var query, status;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        query = EntityQuery.from('ScriptInProgress');
                        query.parameters = { oid: script['Oid'], timeout: 500 };
                        return [4 /*yield*/, script.entityAspect.entityManager.executeQuery(query).then(function (result) { return result.results[0] + ''; })];
                    case 1:
                        status = _a.sent();
                        if (status === 'INITIALIZING' || status === 'RUNNING') {
                            return [2 /*return*/, this.scriptInProgress(script)];
                        }
                        return [2 /*return*/, new Promise(function (resolve, reject) {
                                if (status === 'FINISHED') {
                                    resolve();
                                }
                                reject(new Error('Invalid result from scriptInProgress: ' + status));
                            })];
                }
            });
        });
    };
    return ForecastService;
}(BreezeService));
export { ForecastService };
